/**
 * <infinite-scroll-grid> — progressive reveal of product cards.
 *
 * Renders the first N items visible (server-side via the section's liquid),
 * keeps the rest hidden, and reveals BATCH more each time the user scrolls
 * the bottom sentinel into view. Uses IntersectionObserver — no AJAX, no
 * pagination — every product is already in the DOM, just hidden.
 *
 * Attributes:
 *   data-initial = number of items shown immediately (default 6)
 *   data-batch   = number of items revealed per scroll trigger (default 4)
 *
 * Required descendants:
 *   .infinite-scroll-grid__list (the <ul>)
 *   [data-infinite-scroll-sentinel] (the IntersectionObserver target)
 *   [data-infinite-scroll-loader] (optional — shown while a batch reveals)
 */
class InfiniteScrollGrid extends HTMLElement {
  constructor() {
    super();
    this.batchSize = parseInt(this.dataset.batch, 10) || 4;
    this.initial = parseInt(this.dataset.initial, 10) || 6;
    this.revealed = this.initial;
  }

  connectedCallback() {
    this.list = this.querySelector('.infinite-scroll-grid__list');
    this.items = this.list ? Array.from(this.list.querySelectorAll('.infinite-scroll-grid__item')) : [];
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.loader = this.querySelector('[data-infinite-scroll-loader]');

    if (!this.items.length || !this.sentinel) return;

    if (this.revealed >= this.items.length) {
      this.cleanup();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.revealNext();
          }
        },
        { rootMargin: '300px 0px' }
      );
      this.observer.observe(this.sentinel);
    } else {
      // Old browser fallback — show everything at once.
      this.revealAll();
    }
  }

  revealNext() {
    if (this.loader) this.loader.dataset.active = 'true';

    // Tiny delay so the gradient loader visibly animates between batches.
    window.setTimeout(() => {
      const end = Math.min(this.revealed + this.batchSize, this.items.length);
      for (let i = this.revealed; i < end; i++) {
        const item = this.items[i];
        item.classList.remove('infinite-scroll-grid__item--hidden');
        item.removeAttribute('aria-hidden');
      }
      this.revealed = end;

      if (this.loader) delete this.loader.dataset.active;
      if (this.revealed >= this.items.length) this.cleanup();
    }, 280);
  }

  revealAll() {
    for (const item of this.items) {
      item.classList.remove('infinite-scroll-grid__item--hidden');
      item.removeAttribute('aria-hidden');
    }
    this.revealed = this.items.length;
    this.cleanup();
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.sentinel) this.sentinel.style.display = 'none';
    if (this.loader) this.loader.style.display = 'none';
  }
}

if (!customElements.get('infinite-scroll-grid')) {
  customElements.define('infinite-scroll-grid', InfiniteScrollGrid);
}
